import React, { useState, memo, useEffect } from "react";
import { toast } from "react-toastify";
import SuccessModal from "./SuccessModal";
import { trackFormStart, trackFormSubmission, trackFormSuccess } from "../utils/metaPixel";
// Form Input Component
const FormInput = memo(({ label, error, required, type, name, value, onChange, placeholder, min, max, step, options }) => {
  const inputProps = {
    id: name,
    name,
    value,
    onChange,
    className: `w-full px-4 py-3 rounded-lg border ${error ? 'border-red-500 bg-red-50' : 'border-gray-300'} 
    focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200`,
    placeholder,
    ...(type === 'number' && { min, max, step })
  };

  return (
    <div className="relative">
      <label 
        htmlFor={name} 
        className={`block text-sm font-medium mb-1.5 ${error ? 'text-red-500' : 'text-gray-700'}`}
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="relative">
        {type === 'select' ? (
          <select {...inputProps}>
            <option value="">Sélectionner une option</option>
            {options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        ) : (
          <input type={type} {...inputProps} />
        )}
      </div>
      {error && (
        <p className="mt-1.5 text-sm text-red-500">{error}</p>
      )}
    </div>
  );
});

// Form Section Component
const FormSection = memo(({ title, number, description, children }) => {
  return (
    <div className="bg-white rounded-2xl shadow-lg p-8 transform transition-all duration-300 hover:shadow-xl">
      <div className="flex items-start gap-4 mb-8">
        <div className="flex-shrink-0">
          <span className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-600 text-white font-semibold text-lg">
            {number}
          </span>
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">{title}</h2>
          {description && (
            <p className="text-gray-600">{description}</p>
          )}
        </div>
      </div>
      {children}
    </div>
  );
});


const Form = () => {
  const mentions = ["Passable", "Assez Bien", "Bien", "Très Bien"];
  
  const diplomaTypesBac2 = [
    "DEUG:SMI",
    "DEUG:SMA",
    "DEUG:SMP",
    "DEUG:MIP",
    "DUT",
    "BTS",
    "DTS",
    "Autre"
  ];

  const diplomaTypesBac3 = [
    "Licence Fondamentale",
    "Licence Professionnelle",
    "Licence Sciences et Techniques",
    "Autre"
  ];

  const diplomaTypesBac = [
    "Sciences Mathématiques A",
    "Sciences Mathématiques B",
    "Sciences Physiques",
    "Science de la Vie et de la Terre",
    "Sciences et Technologies Electriques",
    "Science et Technologies Mécaniques",
    "Autre"
  ];
  const initialFormData = {
    nom: '',
    prenom: '',
    dateNaissance: '',
    cin: '',
    codeMassar: '',
    tel: '',
    email: '',
    moyenneBac: '',
    anneeBac: '',
    mentionBac: '',
    typeDiplomeBac: '',
    anneeBac2: '',
    mentionBac2: '',
    typeDiplomeBac2: '',
    specialiteBac2: '',
    anneeBac3: '',
    mentionBac3: '',
    typeDiplomeBac3: '',
    specialiteBac3: ''
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  // Track when user starts the form
  useEffect(() => {
    trackFormStart();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Check if all required fields are filled
    const newFormData = { ...formData, [name]: value };
    const isFormFilled = Object.entries(newFormData).every(([key, value]) => {
      if (key === 'specialiteBac2' || key === 'specialiteBac3') {
        return value.length > 0;
      }
      return value !== '';
    });
    
    setIsDisabled(!isFormFilled);
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate Personal Information
    if (!formData.nom) newErrors.nom = "Le nom est requis";
    if (!formData.prenom) newErrors.prenom = "Le prénom est requis";
    if (!formData.dateNaissance) newErrors.dateNaissance = "La date de naissance est requise";
    if (!formData.cin) newErrors.cin = "Le CIN est requis";
    if (!formData.codeMassar) newErrors.codeMassar = "Le Code Massar est requis";
    if (!formData.tel) newErrors.tel = "Le numéro de téléphone est requis";
    if (!formData.email) {
      newErrors.email = "L'email est requis";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "L'email n'est pas valide";
    }
    
    // Validate BAC
    if (!formData.moyenneBac) newErrors.moyenneBac = "La moyenne du BAC est requise";
    if (!formData.anneeBac) newErrors.anneeBac = "L'année du BAC est requise";
    if (!formData.mentionBac) newErrors.mentionBac = "La mention du BAC est requise";
    if (!formData.typeDiplomeBac) newErrors.typeDiplomeBac = "Le type du BAC est requis";
    
    // Validate BAC+2
    if (!formData.typeDiplomeBac2) newErrors.typeDiplomeBac2 = "Le type du diplôme BAC+2 est requis";
    if (!formData.specialiteBac2) newErrors.specialiteBac2 = "La spécialité du BAC+2 est requise";
    if (!formData.anneeBac2) newErrors.anneeBac2 = "L'année d'obtention du BAC+2 est requise";
    if (!formData.mentionBac2) newErrors.mentionBac2 = "La mention du BAC+2 est requise";

    // Validate BAC+3
    if (!formData.typeDiplomeBac3) newErrors.typeDiplomeBac3 = "Le type du diplôme BAC+3 est requis";
    if (!formData.specialiteBac3) newErrors.specialiteBac3 = "La spécialité du BAC+3 est requise";
    if (!formData.anneeBac3) newErrors.anneeBac3 = "L'année d'obtention du BAC+3 est requise";
    if (!formData.mentionBac3) newErrors.mentionBac3 = "La mention du BAC+3 est requise";
    
    setIsDisabled(Object.keys(newErrors).length > 0);
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    
    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);
      // Track form submission attempt
      trackFormSubmission(formData);
      
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';
        const response = await fetch(`${BACKEND_URL}/api/users`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'authorization': process.env.REACT_APP_JWT_SECRET || 'your-secret-key',
          },
          body: JSON.stringify(formData),
        });
        
        if (response.ok) {
          setFormData(initialFormData);
          setIsSuccessModalOpen(true);
          // Track successful submission
          trackFormSuccess();
        } else {
          throw new Error('Échec de la soumission');
        }
      } catch (error) {
        toast.error('Une erreur est survenue lors de la soumission. Veuillez réessayer.');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setErrors(newErrors);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-16" id="form">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-[#1e253f] mb-4">Rejoignez-nous dans cette aventure passionnante</h2>
          <p className="text-lg text-gray-600 mb-12">
            Façonnez votre avenir dans l'Intelligence Artificielle et l'IoT. Notre programme vous offre une formation d'excellence 
            pour devenir un expert reconnu dans ces domaines innovants.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          <FormSection 
            title="Informations personnelles" 
            number="1"
            description="Vos informations de contact principales"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormInput
                label="Nom"
                type="text"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                error={errors.nom}
                required
                placeholder="Votre nom"
              />
              <FormInput
                label="Prénom"
                type="text"
                name="prenom"
                value={formData.prenom}
                onChange={handleChange}
                error={errors.prenom}
                required
                placeholder="Votre prénom"
              />
              <FormInput
                label="Date de naissance"
                type="date"
                name="dateNaissance"
                value={formData.dateNaissance}
                onChange={handleChange}
                error={errors.dateNaissance}
                required
              />
              <FormInput
                label="CIN"
                type="text"
                name="cin"
                value={formData.cin}
                onChange={handleChange}
                error={errors.cin}
                required
                placeholder="Votre CIN"
              />
              <FormInput
                label="Code Massar"
                type="text"
                name="codeMassar"
                value={formData.codeMassar}
                onChange={handleChange}
                error={errors.codeMassar}
                required
                placeholder="Votre Code Massar"
              />
              <FormInput
                label="Téléphone"
                type="number"
                name="tel"
                value={formData.tel}
                onChange={handleChange}
                error={errors.tel}
                required
                placeholder="0600000000"
              />
              <div className="md:col-span-2">
                <FormInput
                  label="Email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={errors.email}
                  required
                  placeholder="votre.email@exemple.com"
                />
              </div>
            </div>
          </FormSection>

          <FormSection 
            title="Baccalauréat" 
            number="2"
            description="Informations sur votre diplôme du baccalauréat"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <FormInput
                label="Moyenne générale"
                type="number"
                name="moyenneBac"
                value={formData.moyenneBac}
                onChange={handleChange}
                error={errors.moyenneBac}
                required
                placeholder="Ex: 16.00"
                step="0.01"
                min="0"
                max="20"
              />
              <FormInput
                label="Type du diplôme"
                type="select"
                name="typeDiplomeBac"
                value={formData.typeDiplomeBac}
                onChange={handleChange}
                error={errors.typeDiplomeBac}
                required
                options={diplomaTypesBac}
              />
              <FormInput
                label="Année d'obtention"
                type="number"
                name="anneeBac"
                value={formData.anneeBac}
                onChange={handleChange}
                error={errors.anneeBac}
                required
                placeholder="Ex: 2020"
                min="1990"
                max={new Date().getFullYear()}
              />
              <FormInput
                label="Mention obtenue"
                type="select"
                name="mentionBac"
                value={formData.mentionBac}
                onChange={handleChange}
                error={errors.mentionBac}
                required
                options={mentions}
              />
            </div>
          </FormSection>

          <FormSection 
            title="BAC+2" 
            number="3"
            description="Informations sur votre diplôme BAC+2"
          >
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormInput
                  label="Type du diplôme"
                  type="select"
                  name="typeDiplomeBac2"
                  value={formData.typeDiplomeBac2}
                  onChange={handleChange}
                  error={errors.typeDiplomeBac2}
                  required
                  options={diplomaTypesBac2}
                />
                <FormInput
                  label="Spécialité"
                  type="text"
                  name="specialiteBac2"
                  value={formData.specialiteBac2}
                  onChange={handleChange}
                  error={errors.specialiteBac2}
                  required
                  placeholder="Ex: Développement Informatique"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormInput
                  label="Année d'obtention"
                  type="number"
                  name="anneeBac2"
                  value={formData.anneeBac2}
                  onChange={handleChange}
                  error={errors.anneeBac2}
                  required
                  placeholder="Ex: 2022"
                />
                <FormInput
                  label="Mention obtenue"
                  type="select"
                  name="mentionBac2"
                  value={formData.mentionBac2}
                  onChange={handleChange}
                  error={errors.mentionBac2}
                  required
                  options={mentions}
                />
              </div>
            </div>
          </FormSection>

          <FormSection 
            title="BAC+3" 
            number="4"
            description="Informations sur votre diplôme BAC+3"
          >
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormInput
                  label="Type du diplôme"
                  type="select"
                  name="typeDiplomeBac3"
                  value={formData.typeDiplomeBac3}
                  onChange={handleChange}
                  error={errors.typeDiplomeBac3}
                  required
                  options={diplomaTypesBac3}
                />
                <FormInput
                  label="Spécialité"
                  type="text"
                  name="specialiteBac3"
                  value={formData.specialiteBac3}
                  onChange={handleChange}
                  error={errors.specialiteBac3}
                  required
                  placeholder="Ex: Génie Logiciel"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormInput
                  label="Année d'obtention"
                  type="number"
                  name="anneeBac3"
                  value={formData.anneeBac3}
                  onChange={handleChange}
                  error={errors.anneeBac3}
                  required
                  placeholder="Ex: 2023"
                />
                <FormInput
                  label="Mention obtenue"
                  type="select"
                  name="mentionBac3"
                  value={formData.mentionBac3}
                  onChange={handleChange}
                  error={errors.mentionBac3}
                  required
                  options={mentions}
                />
              </div>
            </div>
          </FormSection>

          <div className="flex justify-center pt-8">
            <button
              type="submit"
              disabled={isSubmitting || isDisabled}
              className={`
                group relative px-8 py-4 rounded-xl text-white font-semibold text-lg
                shadow-lg transform transition-all duration-300 overflow-hidden
                flex items-center justify-center gap-3 min-w-[250px]
                ${isSubmitting || isDisabled
                  ? 'bg-blue-400/70 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600'
                }
                before:absolute before:inset-0 before:bg-white/10 before:transform before:scale-x-0 before:opacity-0
                before:origin-left before:transition-transform before:duration-300
                hover:before:scale-x-100 hover:before:opacity-100
                active:scale-95
              `}
            >
              {isSubmitting ? (
                <div className="flex items-center gap-3">
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>Envoi en cours...</span>
                </div>
              ) : (
                <>
                  <span className="relative z-10">Soumettre ma candidature</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className={`w-5 h-5 relative z-10 transition-transform duration-300 
                      ${isSubmitting || isDisabled ? '' : 'group-hover:translate-x-1'}`}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
                    />
                  </svg>
                </>
              )}
            </button>
          </div>
        </form>

        <div className="mt-12 text-center text-gray-600">
          <p className="text-sm">
            En soumettant ce formulaire, vous acceptez que vos données soient traitées conformément à notre politique de confidentialité.
          </p>
        </div>
      </div>
      <SuccessModal 
        isOpen={isSuccessModalOpen} 
        onClose={() => setIsSuccessModalOpen(false)} 
      />
    </div>
  );
};

export default Form;
